import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Trash2, Save, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { needsAnalysisAPI } from '@/lib/api';

const TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'boolean', label: 'Yes / No' },
];

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `q_${Date.now()}`;

export default function NeedsAnalysisRules() {
  const [sets, setSets] = useState(null);
  const [active, setActive] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    needsAnalysisAPI.getQuestionsConfig()
      .then((r) => {
        setSets(r.data.sets);
        setActive(Object.keys(r.data.sets)[0] || '');
      })
      .catch(() => toast.error('Could not load needs-analysis questions'));
  }, []);

  if (!sets) return <div className="flex items-center py-8 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  const pset = sets[active] || { questions: [] };

  const updateQuestion = (idx, patch) => {
    const qs = [...(pset.questions || [])];
    qs[idx] = { ...qs[idx], ...patch };
    setSets({ ...sets, [active]: { ...pset, questions: qs } });
  };
  const updateLabel = (idx, lang, val) => {
    const qs = [...(pset.questions || [])];
    qs[idx] = { ...qs[idx], label: { ...(qs[idx].label || {}), [lang]: val } };
    setSets({ ...sets, [active]: { ...pset, questions: qs } });
  };
  const addQuestion = () => {
    const qs = [...(pset.questions || []), { id: `q_${Date.now()}`, type: 'text', label: { en: '', es: '' } }];
    setSets({ ...sets, [active]: { ...pset, questions: qs } });
  };
  const removeQuestion = (idx) => {
    const qs = (pset.questions || []).filter((_, i) => i !== idx);
    setSets({ ...sets, [active]: { ...pset, questions: qs } });
  };

  const save = async () => {
    // Backfill ids from the English label when missing.
    const clean = { ...sets };
    Object.keys(clean).forEach((k) => {
      clean[k] = {
        ...clean[k],
        questions: (clean[k].questions || []).map((q) => ({
          ...q, id: q.id || slugify(q.label?.en),
        })),
      };
    });
    setSaving(true);
    try {
      const r = await needsAnalysisAPI.updateQuestionsConfig(clean);
      setSets(r.data.sets);
      toast.success('Needs-analysis questions saved');
    } catch {
      toast.error('Could not save questions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="needs-analysis-rules">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ClipboardList size={16} /> Needs Analysis Questions
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={active} onValueChange={setActive}>
            <SelectTrigger className="w-44" data-testid="na-rules-product"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(sets).map((k) => (
                <SelectItem key={k} value={k}>{sets[k].label?.en || k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={save} disabled={saving} data-testid="na-rules-save">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Edit the discovery questions shown per product. Each question has an English and Spanish label.
        </p>
        {(pset.questions || []).map((q, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center border-b border-slate-100 pb-2" data-testid={`na-rule-row-${idx}`}>
            <Input
              className="sm:col-span-5" placeholder="Label (English)"
              value={q.label?.en || ''} onChange={(e) => updateLabel(idx, 'en', e.target.value)}
            />
            <Input
              className="sm:col-span-4" placeholder="Etiqueta (Español)"
              value={q.label?.es || ''} onChange={(e) => updateLabel(idx, 'es', e.target.value)}
            />
            <Select value={q.type || 'text'} onValueChange={(v) => updateQuestion(idx, { type: v })}>
              <SelectTrigger className="sm:col-span-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="sm:col-span-1 text-rose-500" onClick={() => removeQuestion(idx)} data-testid={`na-rule-remove-${idx}`}>
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addQuestion} data-testid="na-rule-add">
          <Plus size={14} className="mr-1" /> Add question
        </Button>
      </CardContent>
    </Card>
  );
}
